import React, { useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { IconButton, Button } from 'components'
import { rootCertificates } from 'tls'
import { CustomEventType } from 'app/types'
import { useAppSelector, useExpirableSessionStorage } from 'app/hooks'
import { getWindowLocation } from 'helpers/config'
import { getNavigationScrollPosition } from 'helpers/navigationScroll'

interface ScrollHelperProps {
    scrollElem: JSX.Element
    pageLink: string,
    onScrollInitialize: (scroll: number) => void
}
const AWAY_THRESHOLD = 200
export function ScrollHelper(props: ScrollHelperProps) {
    const globalInitialized = useAppSelector((state) => state.app.initialized)
    const history = useHistory()
    const { scrollElem, pageLink, onScrollInitialize } = props
    const [direction, setDirection] = useState(-1)
    const [initialized, setInitialized] = useState(false)
    // Use expirable session storage with 30 minute timeout (30 * 60 * 1000 ms)
    const [scroll, setScroll] = useExpirableSessionStorage<number>(`scroll-${pageLink}`, 0, 30 * 60 * 1000)
    const restoredScroll = getNavigationScrollPosition(history.action, scroll)
    const [startScroll, setStartScroll] = useState(restoredScroll)
    const [scrolledAway, setScrolledAway] = useState(0)
    const screen = useAppSelector((state: RootState) => state.app.screen)

    const prevScroll = useRef(0)
    const prevDif = useRef(0)

    // Delay intiailization
    useEffect(() => {
        setInitialized(false)
        const timeout = setTimeout(() => {
            setInitialized(true)
        }, 500)
        return () => {
            clearTimeout(timeout)
        }
    }, [])

    useEffect(() => {
        if (!scrollElem) {
            return
        }

        function checkHash() {
            if (getWindowLocation().hash.length > 1) {
                const id = window.location.hash.substring(1)
                // Check for scroll elem as page loads
                const tryScroll = (tries = 5) => {
                    if (tries == 0) {
                        return
                    }
                    const targetElem = document.getElementById(id)
                    if (targetElem) {
                        targetElem.scrollIntoView({ behavior: 'smooth' })
                    } else {
                        setTimeout(() => {
                            tryScroll(tries - 1)
                        }, 100)
                    }
                }
                setTimeout(() => {
                    tryScroll()
                }, 1000)
            }
        }

        // Set initial scroll if cached
        // Check for hash anchor
        let timeout = null
        if (window.location.hash) {
            // Wait a moment for dynamic content to render
            timeout = setTimeout(() => {
                let finalScroll = restoredScroll
                const elem = document.getElementById(window.location.hash.substring(1))
                if (elem) {
                    finalScroll = elem.offsetTop - 100
                }

                if (onScrollInitialize) {
                    onScrollInitialize(finalScroll)
                }
            }, 100)
        } else if (onScrollInitialize) {
            onScrollInitialize(restoredScroll)
        }

        window.addEventListener('hashchange', checkHash)
        return () => {
            if (timeout) {
                clearTimeout(timeout)
            }
            window.removeEventListener('hashchange', checkHash)
        }
    // Restore only when entering a page or attaching its scroll container.
    // Saving a new position during scrolling must not reinitialize the page.
    }, [scrollElem, pageLink])


    useEffect(() => {
        if (!scrollElem || globalInitialized < 2 || !initialized) {
            return
        }
        const elem = scrollElem
        elem.addEventListener('scroll', handleScroll)
        return () => {
            elem.removeEventListener('scroll', handleScroll)
            // if (scrollElem) {
            // scrollElem.removeEventListener('scroll', handleScroll)
            // }
        }
    }, [scrollElem && scrollElem, direction, globalInitialized, initialized])

    // useEffect(() => {
    //     if (!scrollElem) {
    //         return
    //     }

    //     function checkHash() {
    //         if (getWindowLocation().hash.length > 1) {
    //             const id = getWindowLocation().hash.slice(1, getWindowLocation().hash.length)

    //             switch (id) {
    //                 default:
    //                 case 'register': {
    //                     // Spam scroll into to combat loading page
    //                     // const performScroll = (elem, tries = 100) => {
    //                     //     if (tries == 0) {
    //                     //         return
    //                     //     }
    //                     //     if (elem) {
    //                     //         elem.scrollIntoView({ behavior: 'smooth' })
    //                     //         setTimeout(() => {
    //                     //             performScroll(tries - 1)
    //                     //         }, 50)
    //                     //     }
    //                     // }

    //                     // Check for scroll elem as page loads
    //                     const tryScroll = (tries = 5) => {
    //                         if (tries == 0) {
    //                             return
    //                         }
    //                         const targetElem = document.getElementById(id)
    //                         if (targetElem) {
    //                             // performScroll(targetElem)
    //                             targetElem.scrollIntoView({ behavior: 'smooth' })
    //                         } else {
    //                             setTimeout(() => {
    //                                 tryScroll(tries - 1)
    //                             }, 100)
    //                         }
    //                     }
    //                     setTimeout(() => {
    //                         tryScroll()
    //                     }, 1000)
    //                     break
    //                 }
    //                 case 'thankyou':
    //                     setFinalLink(id)
    //                     break
    //             }
    //         }
    //     }
    //     window.addEventListener('hashchange', checkHash)
    //     checkHash()
    //     return () => {
    //         window.removeEventListener('hashchange', checkHash)

    //     }
    // }, [scrollElem])

    function scrollUp() {
        scrollElem.scrollTo({ top: 0 })//, behavior: 'smooth' })
    }

    function scrollDown() {
        scrollElem.scrollTo({ top: scrollElem.scrollTop + scrollElem.clientHeight, behavior: 'smooth' })
    }

    function handleScroll(e) {
        const newDir = scrollElem.scrollTop > 10 ? 1 : -1
        if (newDir !== direction) {
            setDirection(newDir)
        }

        if (prevScroll.current != null) {
            if (scrolledAway != 1 && scrollElem.scrollTop >= AWAY_THRESHOLD) {
                window.dispatchEvent(new CustomEvent(CustomEventType.ScrollAway, { detail: false }))
                setScrolledAway(1)
            } else if (scrolledAway != -1 && scrollElem.scrollTop < AWAY_THRESHOLD) {
                window.dispatchEvent(new CustomEvent(CustomEventType.ScrollAway, { detail: true }))
                setScrolledAway(-1)
            } else if (scrolledAway == 0 && startScroll > 0) {
                window.dispatchEvent(new CustomEvent(CustomEventType.ScrollAway, { detail: false }))
                setScrolledAway(1)
            }

            /*if (scrollElem.scrollTop < prevScroll.current && prevDif.current > 0) {
                window.dispatchEvent(new CustomEvent(CustomEventType.MenuTutorial))
            }*/
        }

        prevDif.current = scrollElem.scrollTop - prevScroll.current
        prevScroll.current = scrollElem.scrollTop
        setScroll(scrollElem.scrollTop)
    }

    if (!initialized || !scrollElem || scrollElem.offsetHeight >= (scrollElem.scrollHeight - 200)) {
        return null
    }

    const icon = 'fas fa-arrow-down'
    let style = {}
    if (screen.isMobile) {
        style.left = `calc(${document.body.clientWidth / 2}px / var(--zoom)`
    }
    return <div className={`scroll-helper fadeIn${direction == 1 ? ' flip' : ''}`}>
        <IconButton tertiary icon={icon} onClick={direction === 1 ? scrollUp : scrollDown} style={style} />
    </div>
}